import { Box, Grid, Typography } from "@mui/material";
import { CircleUser, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/customButton";
import PaginatedTable from "../../components/dynamicTable";
import TextInput from "../../components/textInput";
import { archiveWorker, updateWorkerById } from "../../services/Workers";
import useWorkerStore from "../../zustand/useWorkerStore";
import { openSnackAlert } from "../../helper";
import AppLoader from "../../components/appLoader";

const employeeTableHeaders = [
  { title: "EMPLOYEE ID", value: "employeeId", align: "center" },
  { title: "NAME", value: "workerName", align: "center" },
  { title: "DESIGNATION", value: "designation", align: "center" },
  { title: "DEPARTMENT", value: "department", align: "center" },
  { title: "EMAIL", value: "email", align: "center" },
  { title: "CHECK IN TIME", value: "checkInTime", align: "center" },
  { title: "CHECK OUT TIME", value: "checkOutTime", align: "center" },
  { title: "STATUS", value: "workerStatus", align: "center" },
  { title: "ARCHIVED", value: "archived", align: "center" },
  { title: "View Details", value: "viewWorkerDetail", align: "center" },
  { title: "ACTION", value: "actions", align: "center" },
];

const displayRows = employeeTableHeaders.map((header) => header.value);

const EmployeeManagement = () => {
  const {
    allWorkers,
    pageLimit,
    currentPage,
    apiLoading,
    searchQuery,
    setPageLimit,
    setCurrentPage,
    setLoading,
    fetchWorkers,
    refreshWorkerData,
  } = useWorkerStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState(searchQuery || "");

  // Backend search (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = search.trim();
      fetchWorkers(term);
      setCurrentPage(0);
    }, search.trim() ? 400 : 0);

    return () => clearTimeout(timer);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageLimit(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleChangeStatus = async (e, row) => {
    try {
      setLoading(true);
      const response = await updateWorkerById(row._id, {
        status: e.target.checked ? "active" : "inactive",
      });
      const resData = response?.data;
      setLoading(false);

      if (resData?.status === "success") {
        await refreshWorkerData();
        openSnackAlert(resData?.message, "success");
      } else {
        openSnackAlert(resData?.message || "Error changing status", "error");
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      openSnackAlert(error?.message || "Something went wrong", "error");
    }
  };

  const handleViewWorkerDetail = (row) => {
    navigate("/employee-profile", { state: { employeeId: row._id } });
  };

  const handleEditWorker = (row) => {
    navigate("/add-employee", { state: { employeeId: row._id } });
  };

  const handleArchiveWorker = async (_e, row) => {
    try {
      setLoading(true);
      const response = await archiveWorker(row._id);
      const resData = response?.data;
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert(resData?.message || "Worker archived successfully", "success");
        await refreshWorkerData();
      } else {
        openSnackAlert(resData?.message || "Error archiving worker", "error");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
      openSnackAlert(error?.message || "Something went wrong while archiving worker", "error");
    }
  };

  const paginatedWorkers = useMemo(() => {
    const start = currentPage * pageLimit;
    const end = start + pageLimit;
    return allWorkers?.slice(start, end) || [];
  }, [allWorkers, currentPage, pageLimit]);

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={apiLoading} />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography variant="h4">Employee Management</Typography>
          <Typography variant="description">
            Admin-only control of employee access and profiles
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <TextInput
            placeholder="Search by name, email or employee ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputBgColor="#fff"
            fullWidth
            borderRadius="22px"
            InputStartIcon={<Search size={18} />}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
            <CustomButton
              btnLabel="Add new employee"
              variant="webbutton"
              btnTextSize={10}
              startIcon={
                <CircleUser color="#ffffff" size={14} strokeWidth={2} />
              }
              handlePressBtn={() => {
                navigate("/add-employee");
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box mt={1}>
        <PaginatedTable
          tableHeader={employeeTableHeaders}
          tableData={paginatedWorkers}
          displayRows={displayRows}
          rowsPerPage={pageLimit}
          currentPage={currentPage}
          totalRecords={allWorkers?.length || 0}
          onEdit={handleEditWorker}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeStatus={handleChangeStatus}
          handleViewWorkerDetail={handleViewWorkerDetail}
          handleArchiveWorker={handleArchiveWorker}
        />
      </Box>
    </Box>
  );
};

export default EmployeeManagement;
